import React, {
  createContext,
  useRef,
  useMemo,
  useCallback,
  useContext,
  useState,
} from 'react'

import type { LayoutRectangle } from 'react-native'

import { withStaticProperties, View, ViewProps, AnimatePresence } from 'tamagui'

const GroupContext = createContext<{
  activeIndex: number | undefined
  setIndex: (newIndex: number) => unknown
  setIndexDimensions: (index: number, dimensions: LayoutRectangle) => unknown
}>({
  activeIndex: undefined,
  setIndex: (newIndex: number) => undefined as void,
  setIndexDimensions: (index: number, dimensions: LayoutRectangle) =>
    undefined as void,
})

const Container = View.styleable<{
  activeIndex: number | undefined
  highlightedElementProps?: ViewProps
  fullWidthHighlight?: boolean
}>(({ activeIndex, children, highlightedElementProps = {}, fullWidthHighlight, ...props }, ref) => {
  const isPointerOver = useRef(false)

  const [itemDimensions, setItemDimensions] = useState<
    Record<number, LayoutRectangle>
  >({})

  const [displayedIndex, setDisplayedIndex] = useState(activeIndex)

  const contextValue = useMemo(
    () => ({
      activeIndex,
      setIndex: (newIndex: number) => {
        setDisplayedIndex(newIndex)
      },
      setIndexDimensions: (index: number, dimensions: LayoutRectangle) => {
        setItemDimensions((oldDimensions) => ({
          ...oldDimensions,
          [index]: dimensions,
        }))
      },
    }),
    [activeIndex, setDisplayedIndex, setItemDimensions]
  )

  const handlePointerEnter = useCallback(() => {
    isPointerOver.current = true
  }, [isPointerOver])

  const handlePointerLeave = useCallback(() => {
    isPointerOver.current = false

    setDisplayedIndex(activeIndex)
  }, [isPointerOver, activeIndex, setDisplayedIndex])

  const indexToUse = isPointerOver.current ? displayedIndex : activeIndex
  const displayedItemDimensions =
    indexToUse !== undefined ? itemDimensions[indexToUse] : undefined

  return (
    <View
      {...props}
      ref={ref}
      cursor='pointer'
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <AnimatePresence>
        {displayedItemDimensions &&
          displayedItemDimensions.height &&
          displayedItemDimensions.width && (
            <View
              key='highlightedElement'
              borderRadius='$3'
              backgroundColor='$lightBlue2'
              {...highlightedElementProps}
              animation='fastSpring'
              position='absolute'
              top={displayedItemDimensions.y}
              height={displayedItemDimensions.height}
              {...(fullWidthHighlight
                ? { left: 0, right: 0 }
                : {
                    left: displayedItemDimensions.x,
                    width: displayedItemDimensions.width,
                  })}
              opacity={1}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
          )}
      </AnimatePresence>

      <GroupContext.Provider value={contextValue}>
        {children}
      </GroupContext.Provider>
    </View>
  )
})

const Item = View.styleable<{
  children: React.ReactNode
  index: number
}>(({ children, index, ...props }, ref) => {
  const group = useContext(GroupContext)

  return (
    <View
      {...props}
      ref={ref}
      onLayout={(event) => {
        group.setIndexDimensions(index, event.nativeEvent.layout)
      }}
      onPointerEnter={() => {
        group.setIndex(index)
      }}
    >
      {children}
    </View>
  )
})

export const Group = withStaticProperties(Container, { Item })
